import getWording from "common/utils/wording";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-extraneous-dependencies
import Chart from "react-apexcharts";
import { useMemo, useEffect, useState } from "react";
import fr from "apexcharts/dist/locales/fr.json";

import Icon from "components/Icon/Icon";
import Table from "common/components/Table/Table";
import fetchJSON from "common/utils/fetchJSON";
import useOrganization from "common/hooks/useOrganization";
import { Constants } from "constants/Constants";

export default function SiteStatistics({
  data,
  isDetail,
  filters,
}: {
  data: any;
  isDetail: boolean;
  filters: any;
}) {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const [statsFromTable, setStatsFromTable] = useState<any[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const { startDate, endDate } = filters;

  const siteDetailStats = useMemo(
    () => [
      {
        name: t("stats.nbThreads"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data && data.totalThreads ? data.totalThreads : 0,
      },
      {
        name: t("stats.nbPosts", { wording: getWording() }),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data && data.totalPosts ? data.totalPosts : 0,
      },
      {
        name: t("stats.nbSubscribersWithAccount"),
        icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
        stat: data && data.followersWithAccount ? data.followersWithAccount : 0,
      },
      ...(Constants.ANONYMOUS_USER_ACTIVE
        ? [
            {
              name: t("stats.nbSubscribersWithoutAccount"),
              icon: <Icon className="w-4 h-4" name="WorkSiteIcon" />,
              stat:
                data && data.followersWithoutAccount
                  ? data.followersWithoutAccount
                  : 0,
            },
          ]
        : []),
    ],
    [data, t]
  );

  const siteThreadsByType = {
    datasets:
      data && data.threadsPerType && data.threadsPerType.length > 0
        ? data?.threadsPerType?.map(
            (thread: { type: string; data: any[] }) => ({
              name: thread.type,
              data: thread?.data?.map((d) => ({
                x: d.date,
                y: d.value,
              })),
              borderWidth: 1,
            })
          )
        : [],
  };

  const postsDataset = {
    labels: data.postsPerSite?.map(
      (post: { date: string; value: number }) => post.date
    ),
    datasets: [
      {
        name: t("stats.newPostsByDate"),
        data:
          data.postsPerSite?.map(
            (post: { date: string; value: number }) => post.value
          ) ?? [],
      },
    ],
  };

  const followersEvolutionData = {
    labels: data.followersEvolution?.map((item: any) => item.date),
    datasets: [
      {
        name: t("stats.nbSubscribersWithAccount"),
        data:
          data.followersEvolution?.map((item: any) => item.withAccount) ?? [],
      },
      ...(Constants.ANONYMOUS_USER_ACTIVE
        ? [
            {
              name: t("stats.nbSubscribersWithoutAccount"),
              data: data.followersEvolution?.map((item: any) => item.anonymous) ?? [],
            },
            {
              name: t("stats.totalSubscribers"),
              data: data.followersEvolution?.map((item: any) => item.total) ?? [],
            },
          ]
        : []),
    ],
  };

  const sortAndIntercalateEntities = (entities: any[]) => {
    if (!entities?.length) return [];

    // 1. Trier tous les chantiers par ordre alphabétique
    const sortedEntities = [...entities].sort((a, b) =>
      a.site.name.localeCompare(b.site.name)
    );

    // 2. Ajouter le préfixe aux chantiers enfants
    const entitiesWithPrefix = sortedEntities.map((entity) => ({
      ...entity,
      site: {
        ...entity.site,
        name: entity.site.parent ? `- ${entity.site.name}` : entity.site.name,
      },
    }));

    // 3. Réorganiser pour intercaler les enfants entre les parents
    const result: any[] = [];

    // Fonction pour trouver tous les enfants d'un site
    const findChildren = (parentId: string) => {
      return entitiesWithPrefix.filter(
        (entity) => entity.site.parent?.id === parentId
      );
    };

    // Parcourir tous les sites
    entitiesWithPrefix.forEach((entity) => {
      if (!entity.site.parent) {
        // Ajouter le site parent
        result.push(entity);

        // Trouver et ajouter tous ses enfants
        const children = findChildren(entity.site.id);
        children.forEach((child) => {
          result.push(child);
        });
      }
    });

    return result;
  };

  // Fetch stats from stat table with a single request
  useEffect(() => {
    const fetchStatsFromTable = async () => {
      if (isDetail || !organization?.id) return;

      setIsLoadingStats(true);
      try {
        // 1. Récupérer tous les sites de l'organisation
        const sitesResponse = await fetchJSON({
          url: `sites?filters[organization][id][$eq]=${organization.id}&filters[active][$eq]=true&filters[disabled][$eq]=false&pagination[limit]=5000&sort=siteType.id:ASC,name:ASC&populate[0]=parent`,
          method: "GET",
        });

        if (!sitesResponse?.data || sitesResponse.data.length === 0) {
          setStatsFromTable([]);
          return;
        }

        const sites = sitesResponse.data;

        // 2. Récupérer les stats agrégées via la route custom
        let statsUrl = `stats/sites/${organization.id}`;
        const params: string[] = [];
        if (startDate) {
          params.push(`startDate=${startDate.toISOString()}`);
        }
        if (endDate) {
          params.push(`endDate=${endDate.toISOString()}`);
        }
        if (params.length > 0) {
          statsUrl += `?${params.join("&")}`;
        }

        const statsBySite = await fetchJSON({
          url: statsUrl,
          method: "GET",
        });

        // 3. Construire le tableau final
        const results = sites.map((site: any) => {
          const siteStats = statsBySite[site.id] || {
            nbExchanges: 0,
            nbPosts: 0,
            lastDaySubscribers: 0,
            lastDayAnonUsers: 0,
          };

          return {
            site: {
              id: site.id,
              name: site.attributes.name,
              parent: site.attributes.parent?.data
                ? {
                    id: site.attributes.parent.data.id,
                  }
                : null,
            },
            nbThreads: siteStats.nbExchanges,
            nbPosts: siteStats.nbPosts,
            nbFollowers:
              siteStats.lastDaySubscribers + siteStats.lastDayAnonUsers,
            followersWithAccount: siteStats.lastDaySubscribers,
            followersWithoutAccount: siteStats.lastDayAnonUsers,
          };
        });

        setStatsFromTable(results);
      } catch (error) {
        console.error("Error fetching stats from table:", error);
        setStatsFromTable([]);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchStatsFromTable();
  }, [isDetail, organization, startDate, endDate]);

  const multipleSitesDataFromStatTable = useMemo(() => {
    if (statsFromTable && statsFromTable.length > 0) {
      return sortAndIntercalateEntities(statsFromTable);
    }
    return [];
  }, [statsFromTable]);

  return (
    <div className="h-5/6">
      {isDetail && (
        <dl className="mt-3 grid grid-cols-1 xl:grid-cols-3 gap-3 mb-3">
          {siteDetailStats.map((item) => (
            <div
              key={item?.name}
              className="relative bg-white pt-3 px-2 sm:pt-2 sm:px-2 shadow rounded-lg overflow-hidden"
            >
              <dt>
                <div className="absolute bg-indigo-500 rounded-md p-3">
                  {item?.icon}
                </div>
                <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                  {item?.name}
                </p>
              </dt>
              <dd className="ml-16 pb-2 flex items-baseline sm:pb-3">
                <p className="text-2xl font-semibold text-gray-900">
                  {item?.stat}
                </p>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {isDetail ? (
        <div className="w-full h-5/6 grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="h-80 justify-center white-box">
            <Chart
              options={{
                chart: {
                  id: "threadsByType",
                  locales: [fr],
                  defaultLocale: "fr",
                },
                title: {
                  text: t("stats.threadsByType"),
                  align: "center",
                  margin: 18,
                  style: {
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#263238",
                  },
                },
                stroke: {
                  curve: "smooth",
                },
                legend: {
                  position: "bottom",
                },
                xaxis: {
                  type: "datetime",
                },
                yaxis: {
                  tickAmount: 5,
                  labels: {
                    formatter: (value) => Math.round(value).toString(),
                  },
                },
                noData: {
                  text: "Aucune données à afficher",
                  align: "center",
                  verticalAlign: "middle",
                  offsetX: 0,
                  offsetY: 0,
                  style: {
                    fontSize: "18px",
                    color: "#263238",
                  },
                },
              }}
              series={siteThreadsByType.datasets}
              type="line"
              height="100%"
              width="100%"
            />
          </div>
          <div className="h-80 justify-center white-box">
            <Chart
              options={{
                chart: {
                  id: "sitePosts",
                  locales: [fr],
                  defaultLocale: "fr",
                },
                title: {
                  text: t("stats.newPostsByDate"),
                  align: "center",
                  margin: 18,
                  style: {
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#263238",
                  },
                },
                stroke: {
                  curve: "straight",
                },
                legend: {
                  position: "bottom",
                },
                xaxis: {
                  type: "datetime",
                  categories: postsDataset.labels,
                },
                yaxis: {
                  tickAmount: 5,
                  labels: {
                    formatter: (value) => Math.round(value).toString(),
                  },
                },
                noData: {
                  text: "Aucune données à afficher",
                  align: "center",
                  verticalAlign: "middle",
                  offsetX: 0,
                  offsetY: 0,
                  style: {
                    fontSize: "18px",
                    color: "#263238",
                  },
                },
                // labels: posts.labels,
              }}
              series={postsDataset.datasets}
              type="bar"
              height="100%"
            />
          </div>
          {/* Followers evolution chart */}
          <div className="w-full">
            <div className="h-80 justify-center white-box">
              <Chart
                options={{
                  chart: {
                    id: "followersEvolution",
                    locales: [fr],
                    defaultLocale: "fr",
                    toolbar: {
                      show: true,
                    },
                  },
                  colors: ["#008FFB", "#00E396", "#775DD0"],
                  dataLabels: {
                    enabled: false,
                  },
                  stroke: {
                    curve: "smooth",
                    width: 2,
                  },
                  title: {
                    text: t("stats.followersEvolution"),
                    align: "center",
                    margin: 18,
                    style: {
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "#263238",
                    },
                  },
                  legend: {
                    position: "bottom",
                  },
                  xaxis: {
                    type: "datetime",
                    categories: followersEvolutionData.labels,
                  },
                  yaxis: {
                    tickAmount: 5,
                    labels: {
                      formatter: (value) => Math.round(value).toString(),
                    },
                  },
                  noData: {
                    text: "Aucune données à afficher",
                    align: "center",
                    verticalAlign: "middle",
                    offsetX: 0,
                    offsetY: 0,
                    style: {
                      fontSize: "18px",
                      color: "#263238",
                    },
                  },
                }}
                series={followersEvolutionData.datasets}
                type="line"
                height="100%"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full gap-3">
          <div className="justify-center white-box">
            <Table
              data={multipleSitesDataFromStatTable ?? []}
              columns={[
                {
                  Header: t("titlePages.sites", {
                    wording: getWording(false, true),
                  }),
                  accessor: "site.name",
                  disableSortBy: true,
                },
                { Header: t("stats.nbThreads"), accessor: "nbThreads" },
                { Header: t("stats.nbPosts"), accessor: "nbPosts" },
                {
                  Header: t("stats.nbSubscribersWithAccount"),
                  accessor: "followersWithAccount",
                },
                ...(Constants.ANONYMOUS_USER_ACTIVE
                  ? [
                      {
                        Header: t("stats.nbSubscribersWithoutAccount"),
                        accessor: "followersWithoutAccount",
                      },
                    ]
                  : []),
              ]}
              isFetching={isLoadingStats}
              pagination={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
